// Radio chat: the dock's Chat tab is the station's one ongoing chat
// (Radio::StationChatService), drawn like a dm_chat conversation. The station
// posts each play's thread into it; those read as "Now playing" markers that
// break message runs, shown for the latest song and for songs someone
// commented on. Other station posts stay in the thread view.

import { type DmChatRun, dmChatDayLabel, dmChatRunFor } from 'lib/dmChat'

export type RadioChatPlay = {
    play_index?: number | null
    play_quest_id?: string | null
    song_answer_id?: string | null
    title?: string | null
}

type RadioChatMessage = {
    id: string | number
    user_id?: string | number | null
    user?: { id?: string | number | null } | null
    created_at?: string | number | Date | null
    is_system?: boolean | null
    is_draft?: boolean | null
    deleted?: boolean | null
    action_tags?: { radio_chat_play?: RadioChatPlay | null } | Record<string, unknown> | null
}

export type RadioChatRow<T extends RadioChatMessage> =
    | { kind: 'message'; message: T; run: DmChatRun; day: string | null }
    | { kind: 'play'; message: T; play: RadioChatPlay; day: string | null }

// Also reads a quoted (embedded) marker, whose JSON is loosely typed.
export function radioChatPlay(
    message: { id?: unknown; action_tags?: unknown } | null | undefined,
): RadioChatPlay | null {
    const play = (message?.action_tags as { radio_chat_play?: RadioChatPlay } | null)
        ?.radio_chat_play
    return play && play.play_quest_id ? play : null
}

export function radioChatRows<T extends RadioChatMessage>(
    messages: T[],
    now: Date = new Date(),
): RadioChatRow<T>[] {
    const rows: RadioChatRow<T>[] = []
    let previous: T | null = null
    // The message a new one may continue; a play marker in between ends the run.
    let previousMessage: T | null = null

    const shown = messages.filter(message => {
        if (!message || message.is_draft || message.deleted) return false
        return !message.is_system || !!radioChatPlay(message)
    })

    // The latest song always shows: it is what is on air.
    let latestPlay: T | null = null
    for (const message of shown) if (radioChatPlay(message)) latestPlay = message

    for (const [index, message] of shown.entries()) {
        const play = radioChatPlay(message)
        // An earlier song shows only once someone commented while it played:
        // the next thing after its marker is a message, not another marker.
        if (play && message !== latestPlay) {
            const next = shown[index + 1]
            if (!next || radioChatPlay(next)) continue
        }

        const day = dmChatDayLabel(previous, message, now)
        if (play) {
            rows.push({ kind: 'play', message, play, day })
            previousMessage = null
        } else {
            rows.push({
                kind: 'message',
                message,
                run: dmChatRunFor(previousMessage, message),
                day,
            })
            previousMessage = message
        }
        previous = message
    }
    return rows
}

function createdAtMs(message: RadioChatMessage): number {
    const ms = message.created_at == null ? NaN : new Date(message.created_at).getTime()
    return Number.isFinite(ms) ? ms : 0
}

// Folds a page into what the dock already shows: one copy per id (the newer
// read wins, so edits land), ordered by created_at like the server pages it.
export function mergeRadioChatMessages<T extends RadioChatMessage>(
    current: T[],
    incoming: T[],
): T[] {
    const byId = new Map<string, T>()
    for (const message of current) byId.set(String(message.id), message)
    for (const message of incoming) byId.set(String(message.id), message)
    return Array.from(byId.values()).sort((a, b) => createdAtMs(a) - createdAtMs(b))
}

function isBefore(message: RadioChatMessage, boundary: RadioChatMessage): boolean {
    const a = createdAtMs(message)
    const b = createdAtMs(boundary)
    return a < b || (a === b && String(message.id) < String(boundary.id))
}

// A fresh read of the latest page replaces everything it covers: from its
// oldest message on (or everything, when it is the whole chat). Messages
// deleted or archived since they were loaded drop out; older history stays.
export function reconcileRadioChatWindow<T extends RadioChatMessage>(
    current: T[],
    latest: T[],
    coversAll: boolean,
): T[] {
    const boundary = latest[0]
    const kept = coversAll || !boundary ? [] : current.filter(m => isBefore(m, boundary))
    return mergeRadioChatMessages(kept, latest)
}
